import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { MapLayoutRefusal } from './mapEditorUnreal';

const sameOrder = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export interface MapEditorContextValue {
  // The draft. Every edit below is local to this author — nothing reaches Unreal, and
  // therefore nothing reaches other participants, until publishLayout is called.
  // Duplicates are allowed and meaningful (two galleries in a row), so this is a list.
  chain: string[];
  appendRoom: (elementId: string) => void;
  removeRoomAt: (index: number) => void;
  moveRoom: (index: number, direction: -1 | 1) => void;
  clearChain: () => void;

  // Throws the draft away and goes back to the layout that is standing. The recovery path out of
  // Clear, which otherwise leaves an author with an empty list, a world Unreal has hidden from them,
  // and no record of what was there.
  revertDraft: () => void;
  // There is something standing to go back to, and the draft is not already it.
  canRevert: boolean;

  // The draft as it stood at the last publish. Null means nothing has been published
  // from this session yet.
  publishedRoomIds: string[] | null;
  // Records a publish. Called by the inspector alongside the SPAWN_LAYOUT send.
  markPublished: (roomIds: string[]) => void;
  // Draft differs from what was last published — i.e. this author is looking at
  // something the other participants are not.
  isDirty: boolean;

  // Room ids Unreal reports as actually standing (CURRENT_GEOMETRY_LIST). This is what
  // everyone currently sees, which is shorter than the published draft when Unreal
  // refused a room for overlapping one already placed.
  builtRoomIds: string[] | null;
  setBuiltRoomIds: (roomIds: string[] | null) => void;

  // Rooms the authority turned down in the same report, keyed by their position in the chain that
  // was published. This is the only thing that distinguishes "Unreal built fewer rooms than I asked
  // for" from "I asked for fewer rooms" — without it a refused room simply vanishes with no reason.
  refusals: MapLayoutRefusal[] | null;
  setRefusals: (refusals: MapLayoutRefusal[] | null) => void;

  // Adopts the layout saved on the branding record as the starting draft. Unlike the
  // CURRENT_GEOMETRY_LIST seed this survives a refresh and reaches a second author on
  // another machine, because it is the author's saved intent rather than a report of what
  // one Unreal instance happens to have standing right now.
  hydrateFromPersisted: (roomIds: string[]) => void;

  // True once the author has changed the chain themselves. A restored or seeded chain describes a
  // layout that already exists elsewhere, so it is not something to build locally — previewing it
  // duplicated whatever the server was already showing.
  hasAuthorEdits: boolean;
}

const MapEditorContext = createContext<MapEditorContextValue | null>(null);

export const MapEditorProvider = ({ children }: { children: ReactNode }) => {
  const [chain, setChain] = useState<string[]>([]);
  const [publishedRoomIds, setPublishedRoomIds] = useState<string[] | null>(
    null,
  );
  const [builtRoomIds, setBuiltRoomIdsState] = useState<string[] | null>(null);
  const [refusals, setRefusals] = useState<MapLayoutRefusal[] | null>(null);

  // Unreal reports the standing layout after any spawn or clear, including ones this
  // author did not make. The first such report is adopted as the starting draft so an
  // author opening the editor begins from what is actually built rather than an empty
  // list — but only once, and only while they have not started editing, so a report
  // arriving mid-edit can never overwrite work in progress.
  const hasSeededRef = useRef(false);
  const hasEditedRef = useRef(false);

  // Whether this author has published from this session. Until they have, CURRENT_GEOMETRY_LIST is
  // the only account of what is standing, so every report is allowed to set the baseline. Once they
  // publish, their own intent takes over — see setBuiltRoomIds and markPublished.
  const hasPublishedRef = useRef(false);

  // Mirrors hasEditedRef as state, because two things outside this provider have to react to it.
  //
  // A restored or seeded chain is not a draft — it is a description of a layout that already
  // exists elsewhere. Previewing it put the author's local copy on top of the rooms the server
  // was already showing them, which is the doubled geometry seen on entry. Only edits the author
  // actually made are worth building locally.
  const [hasAuthorEdits, setHasAuthorEdits] = useState(false);

  const setBuiltRoomIds = useCallback((roomIds: string[] | null) => {
    setBuiltRoomIdsState(roomIds);

    if (!roomIds) {
      return;
    }

    // Recording what is standing is a different job from seeding the draft, and it must not be
    // gated on it. hydrateFromPersisted wins the seed almost every time — the branding fetch beats
    // a round trip through Unreal — and while both jobs shared hasSeededRef, the report could never
    // do the one thing only it is allowed to do. Every client that had not published in this
    // session therefore read a standing layout as an unsaved draft, and said so in the inspector.
    //
    // Not conditioned on publishedRoomIds being unset either: a first report of an empty world
    // (a server that restarted before anyone republished) is a legitimate baseline, and the report
    // that follows the entry republish has to be able to replace it.
    if (!hasPublishedRef.current) {
      setPublishedRoomIds(roomIds);
    }

    if (hasSeededRef.current || hasEditedRef.current) {
      return;
    }
    hasSeededRef.current = true;
    setChain(roomIds);
  }, []);

  // Same one-shot discipline as the geometry seed, and it shares hasSeededRef with it so the
  // two can never fight: whichever arrives first defines the starting draft, and the other is
  // ignored rather than overwriting it. Editing locks both out for the rest of the session.
  //
  // Deliberately does NOT mark the layout published. The branding record holds the author's last
  // saved *intent*, which is not evidence that the world was ever built from it — a save can be
  // written while the spawn fails, and a server can be cleared or restarted afterwards. Treating
  // intent as proof disabled Save on a restored chain, leaving an author looking at rooms they
  // could not republish because the button was dead. Only CURRENT_GEOMETRY_LIST, which reports
  // what Unreal actually has standing, is allowed to mark a layout published.
  const hydrateFromPersisted = useCallback((roomIds: string[]) => {
    if (hasSeededRef.current || hasEditedRef.current) {
      return;
    }
    hasSeededRef.current = true;
    setChain(roomIds);
  }, []);

  const editChain = useCallback((update: (prev: string[]) => string[]) => {
    hasEditedRef.current = true;
    setHasAuthorEdits(true);
    setChain(update);
  }, []);

  const appendRoom = useCallback(
    (elementId: string) => {
      if (!elementId) {
        return;
      }
      editChain((prev) => [...prev, elementId]);
    },
    [editChain],
  );

  const removeRoomAt = useCallback(
    (index: number) => {
      editChain((prev) => prev.filter((_, i) => i !== index));
    },
    [editChain],
  );

  const moveRoom = useCallback(
    (index: number, direction: -1 | 1) => {
      editChain((prev) => {
        const target = index + direction;
        if (
          index < 0 ||
          index >= prev.length ||
          target < 0 ||
          target >= prev.length
        ) {
          return prev;
        }
        const next = [...prev];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      });
    },
    [editChain],
  );

  // Empties the draft only. The standing layout is untouched until the author saves —
  // at which point an empty draft publishes as a clear, since SPAWN_LAYOUT carries
  // clearExisting.
  const clearChain = useCallback(() => {
    editChain(() => []);
  }, [editChain]);

  // What reverting goes back to: what Unreal reports standing, falling back to the last published
  // intent only when no report has arrived yet. The two differ whenever a room was refused, and
  // going back to intent would restore rows that are not in the world — the opposite of matching it.
  const revertTarget = builtRoomIds ?? publishedRoomIds;

  // Discards the draft and puts the standing layout back in the list.
  //
  // Needed because Clear is otherwise a one-way door: it empties the list, and Unreal hides the
  // published rooms for as long as any draft is up — so the author is left looking at an empty world
  // with no record of what they had and nothing to restore it from.
  //
  // Deliberately not routed through editChain: clearing hasAuthorEdits is the point. It lets the
  // preview bridge take the local draft down and hand the real rooms back, instead of building a
  // second copy of geometry the server is already showing.
  const revertDraft = useCallback(() => {
    if (!revertTarget) {
      return;
    }
    hasEditedRef.current = false;
    setHasAuthorEdits(false);
    setChain(revertTarget);
  }, [revertTarget]);

  const canRevert = !!revertTarget && !sameOrder(chain, revertTarget);

  // Records what this author asked for, which from here on outranks what Unreal reports standing.
  // The two differ whenever a room was refused, and letting the report win there would re-raise
  // "unsaved" the instant a publish came back short — telling the author their change had not been
  // applied when it had been, as far as it could be. The refused rows say so individually instead.
  const markPublished = useCallback((roomIds: string[]) => {
    hasPublishedRef.current = true;
    setPublishedRoomIds([...roomIds]);
  }, []);

  // Before the first publish there is nothing to compare against, so any non-empty
  // draft counts as unsaved. An empty draft that has never been published is not
  // dirty — there is nothing to send.
  // hasAuthorEdits matters in the no-baseline branch: emptying a restored chain leaves nothing to
  // compare and nothing in the list, which read as "clean" and disabled Save — so the author could
  // not publish the clear that would take the standing rooms down. Having edited at all is enough
  // to mean there is something to send.
  const isDirty = publishedRoomIds
    ? !sameOrder(chain, publishedRoomIds)
    : hasAuthorEdits || chain.length > 0;

  const value = useMemo<MapEditorContextValue>(
    () => ({
      chain,
      appendRoom,
      removeRoomAt,
      moveRoom,
      clearChain,
      revertDraft,
      canRevert,
      publishedRoomIds,
      markPublished,
      isDirty,
      builtRoomIds,
      setBuiltRoomIds,
      refusals,
      setRefusals,
      hydrateFromPersisted,
      hasAuthorEdits,
    }),
    [
      chain,
      appendRoom,
      removeRoomAt,
      moveRoom,
      clearChain,
      revertDraft,
      canRevert,
      publishedRoomIds,
      markPublished,
      isDirty,
      builtRoomIds,
      setBuiltRoomIds,
      refusals,
      hydrateFromPersisted,
      hasAuthorEdits,
    ],
  );

  return (
    <MapEditorContext.Provider value={value}>
      {children}
    </MapEditorContext.Provider>
  );
};

export const useMapEditor = (): MapEditorContextValue => {
  const ctx = useContext(MapEditorContext);
  if (!ctx) {
    throw new Error('useMapEditor must be used inside MapEditorProvider');
  }
  return ctx;
};
